import { Card, CardContent } from "@/components/ui/card"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { useEffect, useState } from "react"
import { getConsumoEmpresa } from "@/services/companyService"
import { EmpresaConsumo } from "@/types/company"
import { Skeleton } from "@/components/ui/skeleton"
import { ProgressoDetailsModal } from "@/modules/Progress/components/ProgressoDetailsModal"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"

const Progresso = () => {
  const { user } = useAuth()

  const [consumo, setConsumo] = useState<EmpresaConsumo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)

  useEffect(() => {
    if (user?.empId) {
      getConsumoEmpresa(user.empId)
        .then((response) => {
          setConsumo(response.data)
          setLoading(false)
        })
        .catch(() => {
          setError("Erro ao buscar dados de consumo.")
          setLoading(false)
        })
    }
  }, [user])

  // Mapeamento atualizado para incluir 'disponivel'
  const stats =
    consumo.map((item) => ({
      id_grupo: item.id_grupo,
      label: item.descricao,
      contratado: Number(item.contratado),
      consumido: Number(item.consumido),
      disponivel: Number(item.disponivel),
    })) || []

  const handleCardClick = (id_grupo: number) => {
    setSelectedGroupId(id_grupo)
  }

  const handleCloseModal = () => {
    setSelectedGroupId(null)
  }

  return (
    <div>
      <PageHeader
        title="Progresso"
        icon="bars-progress"
      />

      {/* Content */}
      <div className="animate-fade-in space-y-6 px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <Card
                key={index}
                className="border-border bg-surface"
              >
                <CardContent className="space-y-3 p-4">
                  <Skeleton className="h-1 w-full rounded-full bg-muted/30" />
                  <div className="space-y-1">
                    <Skeleton className="h-9 w-1/2 bg-muted/30" />
                    <Skeleton className="h-4 w-full bg-muted/30" />
                    <Skeleton className="h-4 w-2/3 bg-muted/30" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {stats.map((stat) => (
              <Card
                key={stat.id_grupo}
                className={`border-border bg-surface ${
                  stat.consumido === 0 ? "" : "cursor-pointer hover:bg-muted/40"
                } transition-colors`}
                onClick={stat.consumido === 0 ? undefined : () => handleCardClick(stat.id_grupo)}
              >
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Participações
                    </p>
                    {/* Badge de disponibilidade */}
                    <div className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                      {stat.disponivel} Disp.
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-baseline gap-1">
                      <p className="text-4xl font-bold text-foreground">{stat.consumido}</p>
                      <span className="text-xs text-muted-foreground">/ {stat.contratado}</span>
                    </div>
                    <p className="text-sm font-semibold text-muted-foreground">{stat.label}</p>
                  </div>

                  {/* Barra de progresso visual (opcional, baseada no consumo vs total) */}
                  <div className="h-1.5 w-full rounded-full bg-muted/30">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{
                        width: `${Math.min(
                          (stat.consumido / (stat.contratado || 1)) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      {selectedGroupId && (
        <ProgressoDetailsModal
          id_grupo={selectedGroupId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}

export default Progresso
